import _ from 'lodash';
import { Link } from 'react-router-dom';
import { useMemo, ChangeEvent } from 'react';

import {
  Card,
  Table,
  MenuItem,
  TableRow,
  useTheme,
  TableBody,
  TableCell,
  IconButton,
  TableContainer,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { BooleanManager } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { JobFilters } from 'src/services/jobs/jobs.types';
import { ProfileJob, ProfileJobsResponse } from 'src/services/connections/connections.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import VerificationStatusLabel from 'src/components/verification-status-label/verification-status-label';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableSkeleton,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

import JobsTableToolbar from 'src/sections/jobs/table/jobs-table-toolbar';

import { TenantType } from 'src/types/enums';

type Props = {
  loading?: boolean;
  jobs?: ProfileJobsResponse;
  pageOptions: PageOptions;
  onChangePage: (_event: any, pageNumber: number) => void;
  onChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  showTable: BooleanManager;
  filters: Omit<JobFilters, 'page' | 'per_page'>;
  onChangeFilters: (name: keyof JobFilters, value: any) => void;
  onClearFilters: VoidFunction;
};

export default function ConnectionJobsTable({
  loading,
  jobs,
  pageOptions,
  onChangePage,
  onChangeRowsPerPage,
  showTable,
  filters,
  onChangeFilters,
  onClearFilters,
}: Props) {
  const table = useTable();

  const tenant = useOrgTenant();

  const { t } = useTranslate();

  const denseHeight = table.dense ? 56 : 56 + 20;

  const notFound = !jobs?.count;

  const isCandidate = tenant === TenantType.Candidate;

  const tableLabels = useMemo(
    () => [
      { id: 'title', label: t('jobs.table.title') },
      { id: 'vacancies', label: t('jobs.table.vacancies') },
      { id: 'status', label: t('jobs.table.verification_status') },
      { id: '' },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCandidate]
  );

  return (
    <Card>

      {
        loading ?
          (
            <TableSkeleton />
          ) : (
            <>
              <TableContainer sx={{ overflow: 'unset' }}>

                <JobsTableToolbar
                  onFilters={onChangeFilters}
                  clearFilters={onClearFilters}
                  filters={filters as JobFilters}
                  showCreate={false}
                  showImport={false}
                  sx={{
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                />

                <Scrollbar>
                  <Table sx={{ minWidth: 680 }} size={table.dense ? 'small' : 'medium'}>
                    <TableHeadCustom headLabel={tableLabels} />

                    <TableBody>
                      {jobs?.results?.map((row) => <ProfileJobTableRow key={row.id} row={row} />)}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(table.page, table.rowsPerPage, jobs?.count ?? 0)}
                      />

                      <TableNoData notFound={notFound} />
                    </TableBody>
                  </Table>
                </Scrollbar>

              </TableContainer>
              < TablePaginationCustom
                count={jobs?.count ?? 0}
                page={pageOptions.page}
                rowsPerPage={pageOptions.per_page}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
                dense={table.dense}
                onChangeDense={table.onChangeDense}
              />
            </>
          )
      }

    </Card>
  );
}

export function ProfileJobTableRow({ row }: { row: ProfileJob }) {
  const popover = usePopover();

  const { t } = useTranslate();

  const router = useRouter();

  const theme = useTheme();

  const viewDetail = () => {
    router.push(`${paths.dashboard.jobs.root}/${row.id}`);
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Link
            to={`${paths.dashboard.jobs.root}/${row.id}`}
            style={{
              color: theme.palette.text.primary,
              fontWeight: 'bold',
            }}
          >
            {_.capitalize(row.title)}
          </Link>
        </TableCell>

        <TableCell>
          <Label color="primary">{row.vacancies}</Label>
        </TableCell>

        <TableCell>
          {row.verification_status ? (
            <VerificationStatusLabel status={row.verification_status} />
          ) : (
            '-'
          )}
        </TableCell>

        <TableCell align="right" sx={{ pr: 1 }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 200 }}
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            viewDetail();
          }}
        >
          <Iconify icon="solar:eye-bold" />
          {t('common.view')}
        </MenuItem>
      </CustomPopover>
    </>
  );
}
